import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { MailboxDetailFormValue } from '../models/mailbox-detail-form';
import { MailboxDetailSaveModel } from '../models/mailbox-detail-save.model';
import { MailboxDetailView } from '../models/mailbox-detail-view.model';

@Injectable()
export class MailboxDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(private fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();

      this.form = this.fb.group({
         mailbox: this.fb.group({
            emailAddress: this.fb.control(null, [
               Validators.pattern(
                  /^\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*\s*$/i
               ),
               containsCharactersValidator(),
            ]),
            discardAudioAfterEmail: this.fb.control(null),
            markReadAfterEmail: this.fb.control(null),
            isInNameDirectory: this.fb.control(null),
            sayCallerId: this.fb.control(null),
            unavailablePrompt: this.fb.control(null),
            temporaryUnavailablePrompt: this.fb.control(null),
            useTemporaryGreeting: this.fb.control(false),
            mailboxId: this.fb.control(null),
            namePrompt: this.fb.control(null),
            operatorExtension: this.fb.control(null),
            password: this.fb.control(null),
            busyPrompt: this.fb.control(null),
         }),
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateMailbox(mailbox: MailboxDetailView): void {
      if (!mailbox) {
         return;
      }
      const formValue: Partial<MailboxDetailFormValue> = {
         mailbox: {
            emailAddress: mailbox?.emailAddress,
            discardAudioAfterEmail: mailbox?.discardAudioAfterEmail,
            markReadAfterEmail: mailbox?.markReadAfterEmail,
            isInNameDirectory: mailbox?.isInNameDirectory,
            sayCallerId: mailbox?.sayCallerId,
            unavailablePrompt: mailbox.unavailablePrompt,
            temporaryUnavailablePrompt: mailbox.temporaryUnavailablePrompt,
            useTemporaryGreeting: mailbox.temporaryUnavailablePromptId != null,
            mailboxId: mailbox.mailboxId,
            namePrompt: mailbox.namePrompt,
            operatorExtension: mailbox.mailboxOperatorExtension,
            password: mailbox.password ? '0000' : '',
            busyPrompt: mailbox.busyPrompt,
         },
      };

      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(mailboxId: number | null): MailboxDetailSaveModel {
      const value = this.form.value as MailboxDetailFormValue;
      const mailbox: MailboxDetailSaveModel = {
         emailAddress: this.normalizeEmailList(value.mailbox.emailAddress),
         discardAudioAfterEmail: value.mailbox.discardAudioAfterEmail,
         markReadAfterEmail: value.mailbox.markReadAfterEmail,
         isInNameDirectory: value.mailbox.isInNameDirectory,
         sayCallerId: value.mailbox.sayCallerId,
         unavailablePromptId2: value.mailbox.unavailablePrompt?.promptId ?? null,
         temporaryUnavailablePromptId: value.mailbox.useTemporaryGreeting
            ? value.mailbox.temporaryUnavailablePrompt?.promptId ?? null
            : null,
         mailboxId: mailboxId,
         namePromptId: value.mailbox.namePrompt?.promptId ?? null,
         operatorExtensionId: value.mailbox.operatorExtension?.extensionId ?? null,
         password: value.mailbox.password,
         busyPromptId: value.mailbox.busyPrompt?.promptId ?? null,
      };
      return mailbox;
   }

   private normalizeEmailList(value: string | null | undefined): string | null {
      if (value == null) return null;

      const normalized = value
         .split(',')
         .map((email) => email.trim())
         .filter((email) => email.length > 0)
         .join(',');

      return normalized.length ? normalized : null;
   }
}
